import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { axiosInstance } from "../../config";
import { trackSignupPixels } from "../../utils/adPixels";
import { demoLoginCall } from "../../apiCalls";
import "./auth.css";

// Sanitize a returnUrl from the query string. Only same-origin relative paths
// allowed — anything else falls back to /home to prevent open-redirects.
function safeReturnUrl(raw) {
  if (!raw) return "/home";
  if (typeof raw !== "string") return "/home";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/home";
  return raw;
}

const RETURN_URL_STORAGE_KEY = "cb_post_auth_return";

const STEPS = {
  INITIAL: "initial",
  VERIFY_CODE: "verify_code",
};

const AuthPage = () => {
  const { t } = useTranslation('auth');
  const [step, setStep] = useState(STEPS.INITIAL);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Signup happens before the paywall (email or Google → onboarding → paywall).
  const SHOW_GOOGLE = true;
  const [countdown, setCountdown] = useState(0);

  const codeInputs = useRef([]);
  const verifyingRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useContext(AuthContext);

  // Pull returnUrl off the query string so /checkout?... deep-links survive
  // the auth round-trip. Persisted to sessionStorage so the OAuth bounce
  // through Google can recover it on /auth-success.
  const returnUrl = safeReturnUrl(new URLSearchParams(location.search).get("returnUrl"));

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Cursor into the first box on arrival and after a wrong code. Waits for
  // `loading` to clear — the boxes are disabled (unfocusable) until then.
  useEffect(() => {
    if (step === STEPS.VERIFY_CODE && !loading && codeInputs.current[0]) {
      codeInputs.current[0].focus();
    }
  }, [step, loading]);

  const handleGoogleLogin = () => {
    // Stash returnUrl so AuthSuccess can recover it after the Google bounce.
    if (returnUrl && returnUrl !== "/home") {
      sessionStorage.setItem(RETURN_URL_STORAGE_KEY, returnUrl);
    }
    window.location.href = axiosInstance.defaults.baseURL + "/auth/google";
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setError(t('invalidEmail'));
      return;
    }
    setLoading(true);
    setError("");
    try {
      // Demo email bypasses OTP — sign in directly
      if (email.trim().toLowerCase() === "demo@clipbait.ai") {
        await demoLoginCall(dispatch);
        navigate(returnUrl);
        return;
      }
      await axiosInstance.post("/auth/send-code", { email: email.toLowerCase() });
      setStep(STEPS.VERIFY_CODE);
      setCountdown(60);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || t('failedToSendCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      const pasted = value.slice(0, 6).split("");
      const newCode = [...code];
      pasted.forEach((char, i) => {
        if (index + i < 6) newCode[index + i] = char;
      });
      setCode(newCode);
      const nextIndex = Math.min(index + pasted.length, 5);
      if (codeInputs.current[nextIndex]) {
        codeInputs.current[nextIndex].focus();
      }
    } else {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      if (value && index < 5 && codeInputs.current[index + 1]) {
        codeInputs.current[index + 1].focus();
      }
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      if (codeInputs.current[index - 1]) {
        codeInputs.current[index - 1].focus();
      }
    }
  };

  const handleVerifyCode = (e) => {
    e.preventDefault();
    const fullCode = code.join("");
    if (fullCode.length !== 6) {
      setError(t('pleaseEnter6DigitCode'));
      return;
    }
    verifyCode(fullCode);
  };

  // Submits on its own as soon as all 6 digits are in (typed or pasted).
  useEffect(() => {
    const fullCode = code.join("");
    if (step === STEPS.VERIFY_CODE && fullCode.length === 6 && !loading) {
      verifyCode(fullCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const verifyCode = async (fullCode) => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.post("/auth/verify-code", {
        email: email.toLowerCase(),
        code: fullCode,
      });
      const { token, user, isNewUser } = res.data;
      // New account created by this code: browser CompleteRegistration too.
      if (isNewUser) trackSignupPixels(user);
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      dispatch({ type: "LOGIN_SUCCESS", payload: user });
      navigate(returnUrl);
    } catch (err) {
      setError(err.response?.data?.error || t('invalidCode'));
      setCode(["", "", "", "", "", ""]);
    } finally {
      setLoading(false);
      verifyingRef.current = false;
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    setLoading(true);
    setError("");
    try {
      await axiosInstance.post("/auth/send-code", { email: email.toLowerCase() });
      setCountdown(60);
      setCode(["", "", "", "", "", ""]);
    } catch (err) {
      setError(err.response?.data?.error || t('failedToSendCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditEmail = () => {
    setStep(STEPS.INITIAL);
    setCode(["", "", "", "", "", ""]);
    setError("");
  };

  return (
    <div className="auth-page">
      <div className="auth-overlay"></div>

      <a href="https://clipbait.ai" className="auth-page-logo" aria-label="Clipbait home">
        CLIPBAIT
      </a>

      <div className="auth-modal">
        <Link to="/" className="auth-logo">
          {t('common:clipbait')}
        </Link>

        {step === STEPS.INITIAL && (
          <div>
            <h1 className="auth-title">{t('welcomeBack')}</h1>
            <p className="auth-subtitle">{t('signInToContinue')}</p>

            {SHOW_GOOGLE && (
              <>
                <button className="auth-oauth-btn google" onClick={handleGoogleLogin}>
                  <svg className="oauth-icon" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {t('continueWithGoogle')}
                </button>

                <div className="auth-divider">
                  <span>{t('orContinueWithEmail')}</span>
                </div>
              </>
            )}

            <form onSubmit={handleEmailSubmit}>
              <div className="auth-input-group">
                <input
                  type="email"
                  className="auth-input"
                  placeholder={t('enterYourEmail')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>
              {error && <div className="auth-error">{error}</div>}
              <button
                type="submit"
                className="auth-continue-btn"
                disabled={loading || !email}
              >
                {loading ? <div className="auth-spinner"></div> : t('common:continue')}
              </button>
            </form>
          </div>
        )}

        {step === STEPS.VERIFY_CODE && (
          <div>
            <h1 className="auth-title">{t('checkYourEmail')}</h1>
            <p className="auth-subtitle">
              {t('weSentCodeTo')} <strong>{email}</strong>
            </p>

            <div className="auth-email-display">
              <span>{email}</span>
              <button className="auth-edit-btn" onClick={handleEditEmail}>
                {t('common:edit')}
              </button>
            </div>

            <form onSubmit={handleVerifyCode}>
              <div className="auth-code-inputs">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => { codeInputs.current[index] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    className="auth-code-input"
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    disabled={loading}
                  />
                ))}
              </div>
              {error && <div className="auth-error">{error}</div>}
              <button
                type="submit"
                className="auth-continue-btn"
                disabled={loading || code.join("").length !== 6}
              >
                {loading ? <div className="auth-spinner"></div> : t('verifyAndContinue')}
              </button>
            </form>

            <div className="auth-resend">
              {countdown > 0 ? (
                <span className="auth-countdown">{t('resendCodeIn', { countdown })}</span>
              ) : (
                <button
                  className="auth-resend-btn"
                  onClick={handleResendCode}
                  disabled={loading}
                >
                  {t('resendCode')}
                </button>
              )}
            </div>
          </div>
        )}

        <p className="auth-terms">
          {t('byContinuingYouAgree')}{" "}
          <a href="https://clipbait.ai/terms" target="_blank" rel="noopener noreferrer">{t('termsOfService')}</a>{" "}{t('common:and')}{" "}
          <a href="https://clipbait.ai/privacy" target="_blank" rel="noopener noreferrer">{t('privacyPolicy')}</a>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
